<template>
  <form
    class="sm-form sm-form__customer"
    @submit.prevent="handleSubmit"
  >
    <h2>Add New User</h2>
    <div class="sm-form__customer-inner">
      <div class="sm-form__customer-col">
        <h5>Profile</h5>
        <input-text
          v-model="formData.profile.user_name"
          :label="'Username'"
          :name="'user_name'"
          v-validate="'required|min:6|alpha_num'"
          data-vv-as="Username"
          :error="errors.first('user_name')"
          :showLoader="isValidating.user_name"
          @blur="validateDB('user_name')"
          :autocomplete="'off'"
        />
        <div class="sm-form__customer-col-flex">
          <input-text
            v-model="formData.profile.first_name"
            :label="'First name'"
            :name="'first_name'"
            v-validate="'required|first_name_validator|min:2|max:100'"
            data-vv-as="First name"
            :error="errors.first('first_name')"
          />
          <input-text
            v-model="formData.profile.last_name"
            :label="'Last name'"
            :name="'last_name'"
            v-validate="'required|last_name_validator|min:1|max:100'"
            data-vv-as="Last name"
            :error="errors.first('last_name')"
          />
        </div>
        <div class="sm-form__customer-col-flex">
          <input-text
            v-model="formData.profile.email"
            :class="'mobile-full'"
            :label="'Email'"
            :name="'email'"
            v-validate="'required|email'"
            data-vv-as="Email"
            :error="errors.first('email')"
            :showLoader="isValidating.email"
            :isEmail="true"
            @blur="validateDB('email')"
          />
          <input-text
            v-model="formData.profile.additional_email"
            :class="'mobile-full'"
            :label="'Additional email'"
            :name="'additional_email'"
            v-validate="'email'"
            data-vv-as="Additional email"
            :error="errors.first('additional_email') 
                || (formData.profile.email.toLowerCase().trim() 
                    === formData.profile.additional_email.toLowerCase().trim() 
                && formData.profile.additional_email.toLowerCase().trim() !== '' 
                && 'Email and Additional email addresses cannot be the same!')"
            :isEmail="true"
          />
        </div>
         <div class="sm-form__customer-col-flex">
          <input-text
            v-model="formData.profile.password"
            :class="'mobile-full'"
            :type="'password'"
            :label="'Password'"
            :name="'password'"
            v-validate="{
              required: true,
              min: 8,
              regex: passwordRegex
            }"
            data-vv-as="Password"
            :error="errors.first('password')"
            :autocomplete="'off'"
          />
        </div>
        <div class="sm-form__customer-col-flex">
          <input-text
            v-model="formData.profile.company_name"
            :label="'Company name'"
            :name="'company_name'"
            data-vv-as="Company name"
            :error="errors.first('company_name')"
          />
          <phone-input
            v-model="formData.profile.phone"
            :label="'Phone'"
            :placeholder="''"
            :error_text="errors.first('phone') || ''"
            :show_errors="true"
          />
        </div>
      </div>
      <div class="sm-form__customer-col">
        <h5>Address</h5>
        <div class="sm-form__customer-col-flex">
          <country-dropdown
            v-model="selectedCountry"
            :class="'sm-form__customer-col-label'"
            :countryList="countryList"
            :startVal="selectedCountry"
            :error="errors.first('country_id')"
          />
          <input-text
            v-model="formData.address.street"
            :label="'Street'"
            :name="'street'"
            v-validate="'required'"
            data-vv-as="Street"
            :error="errors.first('street')"
          />
        </div>
          <div class="sm-form__customer-col-flex">
            <input-text
              v-model="formData.address.city"
              :label="'City'"
              :name="'city'"
              v-validate="'required'"
              data-vv-as="City"
              :error="errors.first('city')"
            />
            <div class="sm-form__customer-col-flex inner">
              <state-dropdown
                v-if="stateList.length > 0"
                v-model="selectedState"
                :class="'sm-form__customer-col-label'"
                :stateList="stateList"
                :startVal="selectedState"
                :error="errors.first('state_id')"
              />
              <input-zip
                v-model="formData.address.zip_code"
                v-validate="'required|is_zip'"
                :country="selectedCountry"
                :error="errors.first('zip_code')"
              />
            </div>
          </div>
        </div>
    </div>
    
    <span
      v-if="errors.first('base')"
      class="sm-error sm-small"
    >{{errors.first('base')}}</span>
    <btn-block
      :classes="[{disabled: isValidating.user_name || isValidating.email}]"
      :type="'submit'"
      :text="'Add User'"
    />
  </form>
</template>

<script>
import InputText from '@/components/InputText';
import InputZip from '@/components/InputZip';
import PhoneInput from '@/components/PhoneInput';
import DropdownWrap from '@/components/DropdownWrap';
import FormDropdowns from '@/mixins/FormDropdowns';
import GeneralSettings from '@/settings';
import { mapActions, mapGetters } from 'vuex';
import StateDropdown from '@/components/StateDropdown';
import CountryDropdown from '@/components/CountryDropdown';


export default {
  name: 'form-customer-page',
  components: {
    'input-text': InputText,
    'input-zip': InputZip,
    'dropdown-wrap': DropdownWrap,
    'phone-input': PhoneInput,
    'state-dropdown': StateDropdown,
    'country-dropdown': CountryDropdown,
  },
  props: {
    backErrors: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      formData: {
        profile: {
          user_name: '',
          first_name: '',
          last_name: '',
          email: '',
          additional_email: '',
          password: '',
          company_name: '',
          phone: '',
        },
        address: {
          country_id: null,
          state_id: null,
          street: '',
          city: '',
          zip_code: ''
        },
      },
      passwordRegex: GeneralSettings.passwordRegex,
      isValidating: {
        user_name: false,
        email: false
      },
    };
  },
  computed: {
    ...mapGetters({
      provider: 'provider'
    })
  },
  created() {
    this.mixinUserAccount = this.provider;
  },
  mixins: [FormDropdowns],
  methods: {
    ...mapActions({
      validateAccount: 'account/validateAccount',
    }),
    handleSubmit() {
      if (this.errors.items.length || this.isValidating.user_name || this.isValidating.email)
        return;

      // to fix avoid multiple tabs validation issue
      this.validateDB('user_name');
      this.validateDB('email');

      setTimeout(() => {
        if (this.errors.items.length) return;
      
        this.$validator.validate()
          .then(valid => {
            if (!this.formData.profile.phone) {
              this.errors.add({
                field: 'phone',
                msg: 'Enter phone number'
              });
              return false;
            }

            if (!this.formData.address.state_id) {
              if (this.selectedCountry.states.length) {
                this.errors.add({
                  field: 'state_id',
                  msg: 'Please select state'
                });
                return false;
              } else {
                // this.formData.address.state_id = 1; // TEMP, backend requirement
                this.formData.address.state_id = null; 
              }
            }

            const main_email = this.formData.profile.email.toLowerCase().trim();
            const add_email = this.formData.profile.additional_email.toLowerCase().trim();
            if( main_email === add_email && add_email !== '') {
              this.errors.add({
                field: "additional_email",
                msg: 'Email and Additional email addresses cannot be the same!'
              })
              return;
            }

            if (!valid)
              return;

            let data = {...this.formData.address, ...this.formData.profile};
            this.$emit('submit', data);
          })
        }, 1500)
    },
    validateDB(field) {
      if (
        !this.formData.profile[field].length || 
        (this.errors.first(field) && 
        !this.errors.first(field).includes(`\`${this.formData.profile[field]}\``))
      )
        return;

      this.isValidating[field] = true;
      this.validateAccount({
        [field]: this.formData.profile[field]
      })
        .then(() => {
          this.isValidating[field] = false;
        })
        .catch(e => {
          if (e.response.data.errors) {
            let err = e.response.data.errors;
            let msg = '';

            if (err.base) {
              msg = e.response.data.errors.base[0];
            } else {
              Object.keys(err).forEach(key => {
                msg += `${key.toUpperCase()}: ${err[key][0]}\n`;
              });
            }

            this.errors.add({
              field,
              msg
            });
          }

          this.isValidating[field] = false;
        });
    },
  },
  watch: {
    selectedCountry(val) {
      this.formData.address.country_id = val.id;
    },
    selectedState(val) {
      this.errors.remove('state_id');

      if (val)
        this.formData.address.state_id = val.id;
      else
        this.formData.address.state_id = '';
    },
    backErrors(val) {
      if (val.length)
        val.forEach(err => {
          this.errors.add(err);
        });
    },
    provider(val) {
      this.mixinUserAccount = val;
    },
    formData: {
      deep: true,
      handler() {
        // this.errors.remove('base');
      }
    }
    
  }
}
</script>